import { store } from "@/store"
import {
  addresssSuggest,
  addToCart,
  discount,
  orderSubmit,
  setCart,
  setCartCount,
  getCart,
  shipmentUpdate,
} from "@/store/features/cartSlice"
import { getQuantityCount, deleteFromCart } from "@/utils/cart"
import { getShortenedUrlPath } from "@/utils/helper"
import { checkExpPayPal } from "@/components/checkout/v2/utils/payment"

import { FRAUD_DECLINED_MESSAGE_ENDPOINT } from "@/constants/routes"
import { GENERIC_ERROR_MESSAGE, NO_ACTIVE_CART_EXISTS } from "@/constants/"

import {
  validateInputField,
  validateField,
  getFieldValidationMessage,
} from "@/utils/location"

import {
  validatePhone,
  validateCountryCode,
  validateUSPhone,
} from "@/js/helperV2"

import {
  setPaymentFailedAction,
  setPaymentErrorMessageAction,
  setPaymentTechnicalFailedAction,
  setIsPaymentInfoAction,
  setPaymentMethodNameAction,
  setPaymentErrorDetailsAction,
} from "@/store/features/checkoutSlice"
import { setPlaceOrderLoading } from "@/store/features/genericSlice"
import { getAciPaymentErrorMessage } from "@/services/checkoutService"
import { redirectToCartPage } from "@/components/checkout/v2/utils/index"
import {
  getShippingDetails,
  getShippingMethods,
  updateProductDetailToStore,
} from "@/components/cart/cartHelper"
import { getDeviceId } from "@/utils/index"
import {
  addPlaceOrderCCAnalytics,
  placeOrderClickFailed,
  addPlaceOrderDataLayer,
  addAnalyticsForErrorMessages,
} from "@/components/checkout/v2/analytics"

const getGeoLocationDataFromCookie = () => {
  const userLocation =
    document.cookie.match("(^|;)\\s*userLocation\\s*=\\s*([^;]+)")?.pop() || ""
  let geoLocationData = {}
  if (userLocation !== "") {
    const splitLocation = userLocation.split("+")
    if (splitLocation[4] && splitLocation[5]) {
      geoLocationData = {
        lat: splitLocation[4],
        lng: splitLocation[5],
      }
    }
  }
  return geoLocationData
}

export const getAddressSuggestions = async (
  addr,
  useGeoLocation = false,
  logger = () => {}
) => {
  if (!addr || addr.trim() === "") return []
  try {
    let geoLocationData = {}
    // If useGeoLocation is true, get the geo location data from cookie
    if (useGeoLocation) {
      geoLocationData = getGeoLocationDataFromCookie()
    }
    const res = await store.dispatch(addresssSuggest({ addr, geoLocationData }))
    if (res && res.payload && res.payload.results) {
      return res.payload.results.map(item => item.suggestion ?? "")
    } else if (res?.error) {
      throw res.error
    }
    return []
  } catch (err) {
    logger?.warn("Failed to get suggestions from Experian", { error: err })
    throw err
  }
}

export const refreshCart = async () => {
  const cart = store?.getState()?.cart?.cart
  const { lineItems = [], taxedPrice = {} } = cart

  if (lineItems?.length === 0) return
  const shippingZip = localStorage.getItem("shippingZip")

  let payload = {}
  if (shippingZip && shippingZip.trim() !== "" && !taxedPrice) {
    payload = {
      actions: [
        {
          action: "recalculate",
          updateProductData: true,
        },
        {
          action: "calculateTax",
        },
      ],
    }
  } else {
    payload = {
      actions: [
        {
          action: "recalculate",
          updateProductData: true,
        },
      ],
    }
  }
  await store
    .dispatch(addToCart(payload))
    .unwrap()
    .then(res => {
      if (res) {
        store.dispatch(setCart(res))
        const quantityLineItems = res?.lineItems
        const payload = getQuantityCount(quantityLineItems)
        store.dispatch(setCartCount(payload))
      }
    })
}

export const checkInactivePromos = async data => {
  const actions = []
  let actionData

  const { cart: { cart: { discountCodes = [] } = {} } = {} } = store.getState()
  const deviceId = getDeviceId()
  discountCodes
    .filter(item => item.state !== "MatchesCart")
    .map(item => {
      actions.push({
        action: "removeDiscountCode",
        discountCode: { typeId: "discount-code", id: item.discountCode?.id },
      })
      actionData = [...actions]
    })
  if (actionData?.length > 0) {
    store
      .dispatch(discount({ actions: actionData }))
      .unwrap()
      .then(res => {
        if (res && res.type === "Cart") {
          store
            .dispatch(addToCart({ actions: [{ action: "calculateTax" }] }))
            .unwrap()
            .then(resp => {
              if (resp && resp.type === "Cart") {
                store.dispatch(setCart(res))
                store.dispatch(setCartCount(res.lineItems))
                createOrder({ ...data, deviceId })
              }
            })
        }
      })
  } else {
    createOrder({ ...data, deviceId })
  }
}

export const createOrder = async data => {
  const {
    isModalOpen = false,
    isAciCreditCardV2,
    checkoutId,
    deviceId,
    errorMessage,
    logger = () => {},
  } = data || {}

  store.dispatch(setPaymentErrorMessageAction(""))
  store.dispatch(setPaymentFailedAction(false))

  const {
    generic: {
      globalConfig: {
        general: {
          siteName = "",
          orderConfirmPath = "/",
          homepageUrl = "/",
        } = {},
      } = {},
      featureFlags: { ENABLE_ACI_ELAVON_FLOW: isAciElavonFlow = false } = {},
    } = {},
    checkout: { paymentOptionName = "" } = {},
    generic: { globalConfig = {} } = {},
    cart: { paymentId = "" } = {},
  } = store.getState()

  const { apiEndpoints: { orderSubmitAPITimeout = "" } = {} } = globalConfig

  const orderConfirmUrl = getShortenedUrlPath(orderConfirmPath)
  const homePagePath = getShortenedUrlPath(homepageUrl)

  const payload = {
    ...(!isAciCreditCardV2 && { deviceId, paymentId }),
    ...(checkoutId && { sessionId: checkoutId }),
    isAciElavonFlow,
    isAciCreditCardV2,
    orderSource: siteName,
    timeout: orderSubmitAPITimeout,
  }

  store
    .dispatch(orderSubmit(payload))
    .unwrap()
    .then(async res => {
      const {
        parentOrder: { orderNumber = "", customerEmail = "" },
      } = res
      if (res) {
        const base64Str = Buffer.from(customerEmail).toString("base64")
        const url = `${orderConfirmUrl}?orderNumber=${orderNumber}&q=${base64Str}`
        if (paymentOptionName === "card") {
          addPlaceOrderCCAnalytics(paymentOptionName, "success", url)
        } else {
          await addPlaceOrderDataLayer(
            url,
            isModalOpen,
            paymentOptionName,
            "success"
          )
        }

        const isXpp = checkExpPayPal()
        if (paymentOptionName.toLowerCase() === "paypal" && isXpp) {
          localStorage.setItem("paymentMethodXPP", "paypalExpress")
        }
        window.history.replaceState(null, null, homePagePath)
        window.location.href = url
      }
      store.dispatch(setPaymentFailedAction(false))
    })
    .catch(async error => {
      store.dispatch(setPlaceOrderLoading(false))
      const {
        error: errorMsg,
        paymentCompleted = false,
        errorCode = null,
        errorDesc = null,
      } = error
      store.dispatch(
        setPaymentErrorDetailsAction({
          errorCode: errorCode || "",
          errorDesc: errorDesc || "",
        })
      )
      store.dispatch(setPaymentFailedAction(true))
      isAciCreditCardV2 && store.dispatch(setPaymentMethodNameAction("card"))
      if (paymentCompleted) {
        store.dispatch(setPaymentTechnicalFailedAction(true))
        store.dispatch(setIsPaymentInfoAction(true))
        // popup condition
        if (isModalOpen && errorMessage) {
          addAnalyticsForErrorMessages(
            errorMessage,
            "failure",
            isModalOpen,
            "place order",
            "",
            errorCode || "",
            errorDesc || ""
          )
        }
      } else if (errorMsg === "Declined") {
        placeOrderClickFailed("fraud transaction")
        deleteFromCart()
        window.location.href = FRAUD_DECLINED_MESSAGE_ENDPOINT
      } else {
        store
          .dispatch(getCart())
          .unwrap()
          .then(async () => {
            await refreshCart()
            callErrorMessge(error)
          })
      }
      const authorErrorMsg = await getAciPaymentErrorMessage(errorCode)
      const errmsg = authorErrorMsg || errorMsg
      if (!isModalOpen && !paymentCompleted) {
        placeOrderClickFailed(errmsg, errorCode, errorDesc)
      }
      logger?.error("Order submission failed", { error, paymentCompleted })
    })
}

const callErrorMessge = async error => {
  const {
    generic: {
      globalConfig: { general: { checkoutPagePath = "/" } = {} } = {},
    } = {},
    checkout: { cardLastDigit = "" } = {},
  } = store.getState()

  const isXpp = checkExpPayPal()
  const checkoutUrl = getShortenedUrlPath(checkoutPagePath)

  if (isXpp) {
    window.history.replaceState(null, null, checkoutUrl)
  }

  const { error: errorMsg = "", errorCode } = error

  try {
    const authorErrorMsg = await getAciPaymentErrorMessage(errorCode)

    const errorMessage = authorErrorMsg.replace(
      "card-last-4-digits",
      cardLastDigit
    )

    // payment  section open error condition
    store.dispatch(
      setPaymentErrorMessageAction(errorMessage || GENERIC_ERROR_MESSAGE)
    )
  } catch (error) {
    // payment  section open error condition
    const errorMsgNew = errorMsg.replace("card-last-4-digits", cardLastDigit)
    store.dispatch(
      setPaymentErrorMessageAction(errorMsgNew || GENERIC_ERROR_MESSAGE)
    )
  }
}

export const getEventInfo = (type, link, needJson = false) => {
  const eventInfo = {
    eventAction: "checkout:review:" + type,
    eventName: "checkout:review:" + type,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: type,
    internalLinkPosition: "checkout",
    internalLinkType: "checkout:navigation",
    internalLinkZoneName: "checkout:review",
    internalLinkURL: link
      ? (link.indexOf("http") === -1 ? window.location.origin : "") + link
      : "n/a",
    clickInternalLinks: "true",
  }
  if (needJson) {
    return eventInfo
  }
  return JSON.stringify(eventInfo)
}

export const getShippingAddressFormated = data => {
  const {
    city = "",
    country = "",
    email = "",
    firstName = "",
    lastName = "",
    phone = "",
    postalCode = "",
    state = "",
    streetName = "",
    streetNumber = "",
    id = "",
  } = data

  return {
    fname: {
      value: firstName,
      error: false,
      message: "",
    },
    lname: {
      value: lastName,
      error: false,
      message: "",
    },
    email: {
      value: email,
      error: false,
      message: "",
    },
    phone: {
      value: phone,
      error: false,
      message: "",
    },
    address1: {
      value: streetName,
      error: false,
      message: "",
    },
    address2: streetNumber,
    city: {
      value: city,
      error: false,
      message: "",
    },
    state: {
      value: state,
      error: false,
      message: "",
    },
    zipcode: {
      value: postalCode,
      error: false,
      message: "",
    },
    country: {
      value: country,
      error: false,
      message: "",
    },
    notes: "",
    label: {
      show: true,
      value: "",
    },
    id: id,
  }
}

export const getErrorMessage = (genericMessages = [], errorKey = "") => {
  if (errorKey && genericMessages.length) {
    return genericMessages?.find(msg => msg.text === errorKey)?.value ?? ""
  }
  return ""
}

const ERROR_TYPE_MAP = {
  setShippingAddressError: {
    key: "CHECKOUT_SHIPPING_ADDRESS_ERROR",
    i18n: "checkoutShippingAddressError",
  },
  setShippingMethodError: {
    key: "CHECKOUT_SHIPPING_METHOD_ERROR",
    i18n: "checkoutShippingMethodError",
  },
  setCalculateTaxError: {
    key: "CHECKOUT_CALCULATE_TAX_ERROR",
    i18n: "checkoutCalculateTaxError",
  },
}

export const getShippingApiError = (
  genericMessages = [],
  errorType = "",
  i18n = {}
) => {
  const config = ERROR_TYPE_MAP[errorType]
  if (config) {
    return getErrorMessage(genericMessages, config.key) || i18n[config.i18n]
  }

  return getErrorMessage(
    genericMessages,
    errorType || "INVALID_DELIVERY_ADDRESS"
  )
}

export const setShippingAddress = async (
  data = {},
  cb,
  needAnalytics,
  errorCb,
  expPayPalCallBackErrorMsg,
  logger = () => {}
) => {
  const { user = {} } = store.getState()?.auth ?? {}
  const { koc = "", sub: emailFromUser = "" } = user
  const {
    firstName = "",
    lastName = "",
    streetName = "",
    streetNumber = "",
    city = "",
    email = "",
    phone = "",
    postalCode = "",
    state = "",
    country = "",
  } = data

  const isIscUser = Boolean(koc)

  const streetValue =
    typeof streetNumber === "string" ? streetNumber : streetNumber?.value
  const shippingEmail = email || emailFromUser
  const customerEmail = shippingEmail.trim().toLowerCase()

  const address = {
    postalCode: postalCode?.trim(),
    state: state?.trim(),
    country: country?.trim(),
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    streetName: streetName.trim(),
    city: city.trim(),
    email: customerEmail,
    phone: phone.trim(),
    streetNumber: streetValue?.trim(),
  }
  const validationData = validateAllFields(address)
  const { isValid = true } = validationData || {}

  if (!isValid) {
    return errorCb(validationData)
  }

  const actions = [
    {
      action: "setShippingAddress",
      address: address,
    },
    {
      action: "recalculate", // to calculate latest product data from CT cart API call
      updateProductData: true,
    },
  ]
  if (customerEmail) {
    actions.push({
      action: "setCustomerEmail",
      email: customerEmail,
    })
  }
  if (isIscUser) {
    actions.push({
      action: "setCustomType",
      type: { key: "order", typeId: "type" },
      fields: {
        CSRAssociateId: koc,
        orderType: "ISC",
        ...(emailFromUser && { CSREmailId: emailFromUser }),
      },
    })
  }
  const payload = {
    actions: actions,
  }
  await store
    .dispatch(shipmentUpdate(payload))
    .unwrap()
    .then(res => {
      if (res?.type === "Cart" && res.lineItems.length > 0) {
        store.dispatch(setCart(res))
        const quantityLineItems = res?.lineItems
        const payload = getQuantityCount(quantityLineItems)
        store.dispatch(setCartCount(payload))
        getShippingMethods(expPayPalCallBackErrorMsg, errorCb)
        cb && cb(address.postalCode, "success")
        needAnalytics && needAnalytics("success")
      } else {
        if (!phone) {
          cb &&
            cb(address.postalCode, "failure", "failed to set shipping address.")
        }
        redirectToCartPage()
      }
    })
    .catch(error => {
      logger?.error("Failed to set shipping address", { error })
      error.type = "setShippingAddressError"
      errorCb && errorCb(error)
      if (!phone) {
        cb &&
          cb(address.postalCode, "failure", "failed to set shipping address.")
      }
    })
}

export const validateInput = async (
  field = "",
  val = "",
  data = {},
  i18n = {},
  genericMessages = []
) => {
  const value = val?.trim()
  const resolveMessage = (key, fallback) =>
    getErrorMessage(genericMessages, key) || fallback

  const validationMessages = {
    validUSPhone: resolveMessage("VALID_US_PHONE", i18n?.validUSPhone),
    emptyPhoneError: resolveMessage("EMPTY_PHONE_ERROR", i18n?.emptyPhoneError),
    noEmailError: resolveMessage("NO_EMAIL_ERROR", i18n?.noEmailError),
    fnameError: resolveMessage("FIRST_NAME_ERROR", i18n?.fnameError),
    lnameError: resolveMessage("LAST_NAME_ERROR", i18n?.lnameError),
    addressError: resolveMessage("ADDRESS_ERROR", i18n?.addressError),
    cityError: resolveMessage("CITY_ERROR", i18n?.cityError),
    stateError: resolveMessage("STATE_ERROR", i18n?.stateError),
    emptyZipcodeError: resolveMessage(
      "EMPTY_ZIP_CODE_ERROR",
      i18n?.emptyZipcodeError
    ),
    pwdErrorText: resolveMessage("EMPTY_PWD_ERROR", i18n?.pwdErrorText),
    businessTypeError: resolveMessage(
      "BUSINESS_TYPE_ERROR",
      i18n?.businessTypeError
    ),
    genericValidationError: resolveMessage(
      "GENERIC_VALIDATION_ERROR",
      i18n?.nameValidation
    ),
  }

  const { email: { value: emailValue = "" } = {} } = data || {}

  switch (field) {
    case "fname":
    case "lname":
      if (!value) {
        return field === "fname"
          ? validationMessages.fnameError
          : validationMessages.lnameError
      } else if (!(await validateField("name", value))) {
        return getFieldValidationMessage("name") || i18n?.nameValidation
      }
      break
    case "email":
      if (!emailValue) return validationMessages.noEmailError
      else if (!(await validateField("email", value)))
        return getFieldValidationMessage("email") || i18n?.validEmail
      break
    case "phone":
      if (!value) return validationMessages.emptyPhoneError
      if (!validateUSPhone(value))
        return (
          getFieldValidationMessage("phoneNANP") ||
          validationMessages.validUSPhone
        )
      break
    case "address1":
      if (!value) return validationMessages.addressError
      else if (!(await validateField("address", value)))
        return getFieldValidationMessage("address") || i18n?.nameValidation
      break
    case "city":
      if (!value) return validationMessages.cityError
      else if (!(await validateField("city", value)))
        return getFieldValidationMessage("city") || i18n?.nameValidation
      break
    case "state":
      if (!value) return validationMessages.stateError
      break
    case "zipcode":
      if (!value) return validationMessages.emptyZipcodeError
      else if (
        value.length < 5 ||
        !(await validateField("zipCodeNumeric", value))
      )
        return (
          getFieldValidationMessage("zipCodeNumeric") ||
          i18n.invalidZipcodeError
        )
      break
    case "password":
      if (!value) return validationMessages.pwdErrorText
      break
    case "companyName":
      if (!value) return i18n.companyNameError
      else if (!(await validateField("businessName", value)))
        return validationMessages.genericValidationError
      break
    case "businessType":
      if (!value) return validationMessages.businessTypeError
      break
    case "subType":
      if (!value) return i18n.subTypeError
      break
    case "label":
      if (
        value.length > 0 &&
        !(await validateField("saveAddressLabel", value))
      ) {
        return validationMessages.genericValidationError
      }
      break
    default:
      return
  }
}

export const isValidInputData = (field, val = "") => {
  const value = val?.trim()

  switch (field) {
    case "firstName":
    case "lastName":
    case "fname":
    case "lname":
      if (!value || !validateInputField("name", value)) {
        return false
      }
      break
    case "email":
      if (!value) return false
      else if (!validateInputField("email", value)) return false
      break
    case "phone":
      if (!value) return false
      if (!validatePhone(value)) return false
      else if (validateCountryCode(value)) return false
      break
    case "streetName":
    case "address1":
      if (!value) return false
      else if (!validateInputField("address", value)) return false
      break
    case "city":
      if (!value) return false
      else if (!validateInputField("city", value)) return false
      break
    case "state":
      if (!value) return false
      break
    case "postalCode":
    case "zipcode":
      if (!value) return false
      else if (value.length < 5 || !validateInputField("zipCodeNumeric", value))
        return false
      break
    case "password":
      if (!value) return false
      break
    case "companyName":
      if (!value) return false
      else if (!validateInputField("businessName", value)) return false
      break
    case "businessType":
      if (!value) return false
      break
    case "subType":
      if (!value) return false
      break
    case "label":
      if (value.length > 0 && !validateInputField("saveAddressLabel", value)) {
        return false
      }
      break
    default:
      return true
  }
  return true
}

export const getUpdatedCart = async (logger = () => {}) => {
  const payload = {
    actions: [
      {
        action: "recalculate",
        updateProductData: true,
      },
    ],
  }

  try {
    const response = await store.dispatch(addToCart(payload)).unwrap()
    const { error: errorMessage = "" } = response
    if (errorMessage === NO_ACTIVE_CART_EXISTS) {
      redirectToCartPage()
    }
    return response
  } catch (error) {
    const { response: { data: { error: errorMessage = "" } } = {} } = error
    if (errorMessage === NO_ACTIVE_CART_EXISTS) {
      redirectToCartPage()
    }
    logger?.error("Failed to fetch updated cart", { error })
    const { cart: { cart = {} } = {} } = store?.getState() || {}
    return cart
  }
}

export const validateAllFields = address => {
  for (const [field, value] of Object.entries(address)) {
    if (!isValidInputData(field, value)) {
      return {
        isValid: false,
        invalidField: field,
      }
    }
  }

  return {
    isValid: true,
  }
}

export const checkIsShippingRestricted = async () => {
  const shippingResponse = await getShippingDetails()
  updateProductDetailToStore(shippingResponse)

  return shippingResponse?.sku?.length > 0 || false
}
